import React, { useState, useEffect, useCallback } from "react";
import WorkbookTemplateV2 from "../WorkbookTemplateV2";
import { createAttachment } from "../modules/WorkbookTemplateGraphqlHelpers";
import {
  getCurrentCurriculumProgramType,
  getCourseId,
  getSelectedCurriculumId,
  getOrganizationId,
  getUserId,
} from "modules/CommonSelectors";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

import { getAttachment } from "modules/Services";
import { compose, graphql } from "lib/apolloClient";
import { getStaffListQuery } from "modules/CommonQuery";
import { getStaffListFromCache } from "modules/CommonGraphqlHelpers";
import { getFormattedUserName } from "Utils/GetFormattedUserName";

import { CACHE_AND_NETWORK } from "Constants/stringConstants";
import { AVATAR_COLOR_MAP } from "AppComponents/Workbook/WorkbookTemplateCard/modules/WorkbookTemplateCardUtils";
import { connect } from "react-redux";
import I18nHOC from "UIComponents/I18nHOC";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import Workbook from "AppComponents/Workbook";
import {
  WORKBOOK_MODE_TYPES,
  shouldEnableWorkbookTemplateLibrary,
} from "Utils";
import _ from "lodash";
import { Modal, Avatar } from "@toddle-edu/ds-web";
import { isWorkbookParityEnabled } from "Utils/WorkbookHelpers";

const styles = {
  modalStyle: {
    padding: 0,
    marginTop: 0,
    marginBottom: 0,
  },
};

const modalDimensions = {
  cardWidth: 244, // 244 is the width of the card
  modalMaxWidthAdjustment: 80,
  padding: 64,
  sideBarWidth: 240,
  border: 2,
};
const omitKeys = [
  "metadata.oldWorkbookUrls",
  "metadata.updatedBy",
  "metadata.oldUrls",
  "metadata.fileExtension",
  "similarityReport",
  "parentType",
  "title",
  "streamUrl",
  "isRead",
  "questionAttachment",
  "__typename",
  "createdBy",
  "isAnnotationRead",
];

const WorkbookTemplateWrapperV2 = props => {
  const {
    userId,
    courseId,
    curriculumProgramId,
    closeModal,
    onUpdateSuccessfully,
    updateAttachments,
    addAttachment,
    createAttachment,
    portalType,
    curriculumProgramType,
    setToastMsg,
    staffList,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [useTemplate, setUseTemplate] = useState(false);
  const [attachment, setAttachment] = useState({});
  const [modalWidth, setModalWidth] = useState();
  const [templateFilters, setTemplateFilters] = useState({
    searchText: "",
    folderId: "allTemplates",
  });
  const [resourceId, setResourceId] = useState("");
  const [isEditTemplate, setIsEditTemplate] = useState(false);
  const [createdBy, setCreatedBy] = useState([userId]);
  const [isTemplateCreator, setIsTemplateCreator] = useState(false);

  const enableWorkbookTemplateLibrary = shouldEnableWorkbookTemplateLibrary({
    curriculumProgramType,
  });

  const updateWorkbookTemplateFilters = useCallback(data => {
    const { key, value } = data;
    setTemplateFilters(prevFilters => ({ ...prevFilters, [key]: value }));
  }, []);

  const getModalWidth = () => {
    const width = window.innerWidth;
    const {
      cardWidth,
      modalMaxWidthAdjustment,
      padding,
      sideBarWidth,
      border,
    } = modalDimensions;
    const widthWithoutCards =
      padding + (enableWorkbookTemplateLibrary ? sideBarWidth : 0) + border;
    const numberOfCardsToRender = Math.floor(
      (width - widthWithoutCards - modalMaxWidthAdjustment) / cardWidth
    );
    const modalWidth = numberOfCardsToRender * cardWidth + widthWithoutCards;
    setModalWidth(modalWidth);
  };

  const throttledSetModalWidths = _.throttle(getModalWidth, 100);

  useEffect(() => {
    window.addEventListener("resize", throttledSetModalWidths);
    getModalWidth();

    return () => {
      window.removeEventListener("resize", throttledSetModalWidths);
    };
  }, []);

  const onUpdate = async () => {
    const newAttachment = await getAttachment({
      id: attachment.id,
      portalType,
    });
    const tempArr = _.omit(newAttachment, omitKeys);

    addAttachment([tempArr]);
  };

  const onTemplateUpdated = label => {
    setUseTemplate(false);
    setIsEditTemplate(false);
    setAttachment({});
    setResourceId("");
    setIsTemplateCreator(false);
    setToastMsg({
      msg: "workbook:template_update_successfully_with_label",
      type: "success",
      position: "toast-bottom-left",
      locale_params: [{ key: "label", value: label, isPlainText: true }],
    });
  };

  const onTemplateCreated = label => {
    setUseTemplate(false);
    setIsEditTemplate(false);
    setAttachment({});
    setResourceId("");
    setIsTemplateCreator(false);
    setToastMsg({
      msg: "workbook:template_save_successfully_with_label",
      type: "success",
      position: "toast-bottom-left",
      locale_params: [{ key: "label", value: label, isPlainText: true }],
    });
  };

  const createBlankWorkbook = useCallback(async () => {
    setIsLoading(true);
    const newAttachment = await createAttachment({
      attachment: {
        type: "WORKBOOK",
        name: "Workbook",
        metadata: {
          isWorkbookWithPageView: true,
          isNewBackgroundToolbarEnabled: true,
          isNewShapesToolbarEnabled: true,
          numOfPages: 1,
        },
      },
      portalType,
    });
    setIsLoading(false);

    if (!_.isEmpty(newAttachment)) {
      setAttachment(newAttachment[0]);
      setUseTemplate(true);
    }
  }, [createAttachment, portalType]);

  const handleCreateAttachment = useCallback(
    async ({ attachment, isTemplateCreator, resourceId }) => {
      const templateChildAttachmentIds = _.get(
        attachment,
        "metadata.childAttachmentIds",
        []
      );

      const attachmentObject = {
        name: attachment?.name,
        type: attachment?.type,
        url: attachment?.url,
        thumbUrl: attachment?.thumbUrl,
        presetAttachmentId: attachment?.id,
        metadata: {
          ..._.omit(_.get(attachment, "metadata", {}), "childAttachmentIds"),
          templateChildAttachmentIds,
        },
      };

      setIsLoading(true);
      const createdAttachment = await createAttachment({
        attachment: attachmentObject,
        portalType,
      });
      setIsLoading(false);

      if (!_.isEmpty(createdAttachment)) {
        setAttachment(createdAttachment[0]);
        setIsTemplateCreator(isTemplateCreator);
        setResourceId(resourceId);
        setUseTemplate(true);
      }
    },
    [createAttachment, portalType]
  );

  const handleEditAttachment = ({ attachment, resourceId }) => {
    setAttachment(attachment);
    setResourceId(resourceId);
    setIsEditTemplate(true);
    setUseTemplate(true);
  };

  const renderWorkbook = () => (
    <Workbook
      attachment={attachment}
      workbookMode={WORKBOOK_MODE_TYPES["WORKBOOK_MULTIPAGE_ADD_OR_EDIT"]}
      onUpdateSuccessfully={onUpdate}
      onDiscardDirectly={closeModal}
      onTemplateCreated={onTemplateCreated}
      onTemplateUpdated={onTemplateUpdated}
      updateAttachments={updateAttachments}
      portalType={portalType}
      disableArr={["SAVE_BTN"]}
      enableArr={isEditTemplate ? ["UPDATE_TEMPLATE"] : ["SAVE_DROPDOWN"]}
      templateMetadata={{
        curriculumProgramId,
        courseId,
        resourceId,
        canOverwrite: !isEditTemplate && isTemplateCreator,
        showEditFrameOption: isWorkbookParityEnabled(),
      }}
    />
  );

  return useTemplate ? (
    renderWorkbook()
  ) : (
    <Modal onClose={closeModal} width={modalWidth} dsVersion="2.0">
      <Modal.Body isScrollable={true} style={styles.modalStyle}>
        <WorkbookTemplateV2
          closeModal={closeModal}
          onUpdateSuccessfully={onUpdateSuccessfully}
          updateAttachments={updateAttachments}
          handleCreateAttachment={handleCreateAttachment}
          handleEditAttachment={handleEditAttachment}
          createBlankWorkbook={createBlankWorkbook}
          updateWorkbookTemplateFilters={updateWorkbookTemplateFilters}
          searchText={templateFilters.searchText}
          folderId={templateFilters.folderId}
          portalType={portalType}
          createdBy={createdBy}
          setCreatedBy={setCreatedBy}
          staffList={staffList}
          enableWorkbookTemplateLibrary={enableWorkbookTemplateLibrary}
        />
        {isLoading && <FullScreenLoader />}
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const curriculumProgramType =
    getCurrentCurriculumProgramType(state) ||
    _.get(ownProps, "curriculumProgramType");

  return {
    userId: getUserId(state),
    courseId: getCourseId(state),
    organizationId: getOrganizationId(state),
    curriculumProgramId: getSelectedCurriculumId(state),
    curriculumProgramType,
  };
};

const mapActionCreators = {
  createAttachment,
  getAttachment,
  setToastMsg,
};

export default compose(
  I18nHOC({ resource: ["common", "teacherHomePage"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getStaffListQuery, {
    name: "getStaffList",
    options: ({ organizationId }) => ({
      fetchPolicy: CACHE_AND_NETWORK,
      variables: {
        organizationId,
      },
    }),
    props({
      getStaffList: { networkStatus, variables },
      ownProps: { isData, isLoading },
    }) {
      const queryData = getStaffListFromCache(variables);
      const staffList = _.get(queryData, "node.staff.edges", []);
      const getStaffAvatar = ({ id, fullName, profileImage }) => {
        return (
          <Avatar
            dsVersion="2.0"
            shade={"light"}
            name={fullName}
            size={"x-small"}
            shape="circle"
            color={AVATAR_COLOR_MAP[id % _.size(AVATAR_COLOR_MAP)]}
            src={profileImage}
          />
        );
      };
      const staffListOptions = _.map(staffList, staff => {
        const node = _.get(staff, "node", {});
        const id = _.get(node, "id");
        const fullName = getFormattedUserName({ user: node, type: "staff" });
        const profileImage = _.get(node, "profileImage");

        return {
          key: id,
          value: id,
          label: fullName,
          prefixElement: getStaffAvatar({ id, fullName, profileImage }),
        };
      });
      return {
        staffList: staffListOptions,
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  })
)(WorkbookTemplateWrapperV2);
